import { isApiError } from '@lib/api/utils/is-api-error';

/**
 * Returns true when a query/mutation's `meta` opts out of Sentry capture for
 * this error — via `expectedFailure`, `suppressSentryStatuses` (ApiError HTTP
 * status), or `suppressSentryErrorNames` (custom `error.name` for
 * user-facing failures).
 *
 * Shared by `captureTanstackError` and the stale-hydrated-cache observer in
 * `query-client.ts` so a single `meta` declaration silences a known-expected
 * failure across every Sentry path it can reach.
 */
export function shouldSuppressSentryForMeta(
  error: Error,
  meta: Record<string, unknown> | undefined
): boolean {
  if (matchesExpectedFailure(error, meta?.expectedFailure)) return true;

  const statusList = meta?.suppressSentryStatuses;
  if (
    isApiError(error) &&
    Array.isArray(statusList) &&
    statusList.some(status => status === error.status)
  ) {
    return true;
  }

  const nameList = meta?.suppressSentryErrorNames;
  if (
    Array.isArray(nameList) &&
    nameList.some(name => typeof name === 'string' && name === error.name)
  ) {
    return true;
  }

  return false;
}

function matchesExpectedFailure(error: Error, expected: unknown): boolean {
  if (!isApiError(error)) return false;
  if (expected === 'forbidden') return error.status === 403;
  return false;
}
